import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, ShieldCheck, Lock, Copy, Archive, Eye, Search, ArrowLeft, Save, AlertTriangle, RefreshCw, Trash2, GripVertical, LayoutDashboard, Grid3x3 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ACTION_ORDER = ["view_list", "view_detail", "create", "update", "submit", "request", "review", "approve", "reject", "pay", "reconcile", "ship", "close", "assign", "archive", "import", "export", "print", "upload_attachment", "cancel", "delete", "restore"];
const ACTION_LABEL = { view_list: "View", view_detail: "Detail", create: "Create", update: "Edit", submit: "Submit", request: "Request", review: "Review", approve: "Approve", reject: "Reject", pay: "Pay", reconcile: "Recon", ship: "Kirim", close: "Close", assign: "Assign", archive: "Archive", import: "Import", export: "Export", print: "Print", upload_attachment: "Upload", cancel: "Cancel", delete: "Delete", restore: "Restore" };

export default function Roles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === "owner";
  const [roles, setRoles] = useState([]);
  const [editId, setEditId] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const load = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const migrate = async () => {
    setMigrating(true);
    try { const r = await api.post("/roles/migrate-v2"); toast.success(`Migrasi selesai: ${r.data.total} role`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setMigrating(false); }
  };
  const clone = async (r) => { try { const res = await api.post(`/roles/${r.id}/clone`); toast.success("Role diklon"); load(); setEditId(res.data.id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const createNew = async () => {
    const label = window.prompt("Nama role baru:");
    if (!label || !label.trim()) return;
    const copyOwner = window.confirm("Salin seluruh izin Owner sebagai titik awal?\n\nOK = Salin izin Owner (akses penuh, lalu matikan izin yang tidak perlu)\nBatal = Mulai tanpa izin");
    try {
      const res = await api.post("/roles", { label: label.trim(), template: copyOwner ? "owner" : "empty", access_model: copyOwner ? "deny_list" : "empty" });
      toast.success(copyOwner ? "Role dibuat menyalin izin Owner — matikan yang tidak perlu" : "Role dibuat tanpa izin");
      load(); setEditId(res.data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => { if (!window.confirm(`Hapus role "${r.label}"? Tindakan ini permanen dan hanya bisa jika tidak dipakai pengguna.`)) return; try { await api.delete(`/roles/${r.id}`); toast.success("Role dihapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const archive = async (r) => { if (!window.confirm(`Arsipkan role "${r.label}"?`)) return; try { await api.post(`/roles/${r.id}/archive`); toast.success("Role diarsipkan"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (editId) return <RoleEditor rid={editId} canManage={canManage} onBack={() => { setEditId(null); load(); }} />;

  return (
    <div data-testid="roles-page">
      <PageHeader title="Custom Role & Access Control" subtitle="Atur izin granular per module.resource.action, data scope, dan aset sensitif">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/roles/matrix")} className="border-taupe text-cocoa" data-testid="roles-matrix-btn"><Grid3x3 className="w-4 h-4 mr-1.5" />Matriks Izin</Button>
          {canManage && <>
            <Button variant="outline" onClick={migrate} disabled={migrating} className="border-taupe text-cocoa" data-testid="roles-migrate-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Sinkronkan Izin</Button>
            <Button onClick={createNew} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="roles-create-btn"><Plus className="w-4 h-4 mr-1.5" />Buat Role</Button>
          </>}
        </div>
      </PageHeader>
      <Panel>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Nama Role</TableHead><TableHead>Tipe</TableHead><TableHead className="text-center">Pengguna</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {roles.filter((r) => !r.archived).map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => setEditId(r.id)} data-testid={`role-row-${r.key}`}>
                <TableCell className="font-semibold text-cocoa"><span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-taupe" />{r.label}</span></TableCell>
                <TableCell>{r.builtin ? <span className="inline-flex items-center gap-1 text-xs text-wmuted"><Lock className="w-3 h-3" /> Business role</span> : <span className="text-xs font-medium text-status-passed">Custom role</span>}</TableCell>
                <TableCell className="text-center text-sm">{r.user_count}</TableCell>
                <TableCell className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 text-cocoa" onClick={() => setEditId(r.id)} data-testid={`edit-role-${r.key}`}><Pencil className="w-4 h-4" /></Button>
                  {canManage && <Button size="sm" variant="ghost" className="h-7 text-cocoa" onClick={() => clone(r)} data-testid={`clone-role-${r.key}`}><Copy className="w-4 h-4" /></Button>}
                  {canManage && r.key !== "owner" && !r.protected && <Button size="sm" variant="ghost" className="h-7 text-status-reject" onClick={() => del(r)} data-testid={`delete-role-${r.key}`}><Trash2 className="w-4 h-4" /></Button>}
                  {canManage && !r.builtin && <Button size="sm" variant="ghost" className="h-7 text-amber-600" onClick={() => archive(r)} data-testid={`archive-role-${r.key}`}><Archive className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!roles.length && <TableRow><TableCell colSpan={4} className="text-center text-wmuted py-8">Belum ada role.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}

function RoleEditor({ rid, canManage, onBack }) {
  const [cat, setCat] = useState(null);
  const [detail, setDetail] = useState(null);
  const [perms, setPerms] = useState(new Set());
  const [scope, setScope] = useState({ organization: "all", channels: [], warehouses: [] });
  const [label, setLabel] = useState("");
  const [tab, setTab] = useState("permissions");
  const [selModule, setSelModule] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const readOnly = !canManage || detail?.key === "owner";

  const reload = () => Promise.all([
    api.get("/roles/catalog-v2"),
    api.get(`/roles/${rid}/detail-v2`),
  ]).then(([c, d]) => {
    setCat(c.data); setDetail(d.data); setPerms(new Set(d.data.perms_v2)); setLabel(d.data.label || "");
    setScope(d.data.data_scope || { organization: "all", channels: [], warehouses: [] });
    setSelModule((m) => m || c.data.modules[0]?.key);
  }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [rid]);

  const has = (k) => perms.has(k);
  const toggle = (k, v) => setPerms((s) => { const n = new Set(s); v ? n.add(k) : n.delete(k); return n; });
  const setMany = (keys, v) => setPerms((s) => { const n = new Set(s); keys.forEach((k) => v ? n.add(k) : n.delete(k)); return n; });

  const modules = cat?.modules || [];
  const permByModule = useMemo(() => {
    const map = {};
    (cat?.permissions || []).filter((p) => p.module !== "sensitive").forEach((p) => { (map[p.module] = map[p.module] || []).push(p.key); });
    return map;
  }, [cat]);
  const moduleCount = (mk) => { const all = permByModule[mk] || []; return `${all.filter((k) => perms.has(k)).length}/${all.length}`; };

  const applyPreset = async (mk, preset) => {
    const mod = modules.find((m) => m.key === mk);
    const resources = mod.resources.map((r) => r.key).join(",");
    const modKeys = permByModule[mk] || [];
    if (preset === "clear") { setMany(modKeys, false); return; }
    try { const r = await api.get("/roles/preset-perms", { params: { preset, resources } }); setMany(modKeys, false); setMany(r.data.perms, true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const isDeny = detail?.access_model === "deny_list";
      const allKeys = (cat?.permissions || []).map((p) => p.key || p);
      const body = isDeny
        ? { access_model: "deny_list", deny_v2: allKeys.filter((k) => !perms.has(k)), data_scope: scope, label: label.trim() }
        : { perms_v2: [...perms], data_scope: scope, label: label.trim() };
      const r = await api.put(`/roles/${rid}/permissions-v2`, body);
      const allowedN = (r.data.added || r.data.denied_removed || []).length;
      const blockedN = (r.data.removed || r.data.denied_added || []).length;
      toast.success(`Tersimpan (${allowedN} diizinkan, ${blockedN} dibatasi, ${r.data.affected_users} user terdampak)`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!cat || !detail) return <div className="p-6 text-wmuted">Memuat…</div>;
  const activeCount = [...perms].filter((k) => !k.startsWith("sensitive.")).length;
  const sensCount = [...perms].filter((k) => k.startsWith("sensitive.")).length;
  const curMod = modules.find((m) => m.key === selModule);

  return (
    <div className="space-y-4" data-testid="role-editor">
      {detail.access_model === "deny_list" && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="role-denylist-banner">Role ini <b>menyalin izin Owner</b>. ✅ Tercentang = diizinkan · ☐ Hapus centang = diblokir. Klik Simpan untuk menerapkan.</div>}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="text-cocoa" data-testid="role-editor-back"><ArrowLeft className="w-4 h-4 mr-1" />Kembali</Button>
          <PageHeader title={`Edit role: ${detail.label}`} subtitle="Atur tindakan, batas data, dan aset sensitif yang dapat diakses role ini." />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} className="border-taupe text-cocoa" data-testid="role-preview-user-btn"><Eye className="w-4 h-4 mr-1" />Preview User</Button>
          {!readOnly && <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="role-save-btn"><Save className="w-4 h-4 mr-1" />Simpan Perubahan</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Nama role" value={readOnly || detail.builtin ? detail.label : <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="role-name-input" className="bg-transparent border-b border-taupe/50 focus:outline-none focus:border-cocoa text-cocoa font-semibold w-full" placeholder="Nama role" />} badge={detail.builtin ? "Business role" : "Custom role"} />
        <Card label="Permission aktif" value={<span>{activeCount + sensCount} dari {detail.total_permissions}<span className="block text-[10px] text-wmuted font-normal mt-0.5">{activeCount} izin modul + {sensCount} aset sensitif</span></span>} />
        <Card label="Cakupan default" value={scope.organization === "all" ? "All division" : scope.organization} />
        <Card label="Risiko akses" value={sensCount ? <span className="inline-flex items-center gap-1 text-status-reject"><AlertTriangle className="w-4 h-4" />{sensCount} sensitif</span> : "Aman"} />
      </div>

      <div className="flex gap-1 border-b border-taupe/20 flex-wrap">
        {[["permissions", "Permissions"], ["scope", "Data Scope"], ["sensitive", "Aset Sensitif"], ["dashboard", "Konfigurasi Dashboard"], ["users", `Assigned Users ${detail.assigned_count}`], ["history", "Riwayat Perubahan"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm font-medium ${tab === k ? "text-cocoa border-b-2 border-cocoa" : "text-wmuted"}`} data-testid={`role-tab-${k}`}>{l}</button>
        ))}
      </div>

      {readOnly && detail.key === "owner" && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded px-3 py-2">System Owner memiliki seluruh akses dan tidak dapat diubah (terproteksi).</div>}

      {tab === "permissions" && (
        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          <Panel className="p-2">
            <div className="relative mb-2"><Search className="w-4 h-4 absolute left-2 top-2.5 text-wmuted" /><Input className="pl-8 bg-white h-9" placeholder="Cari modul…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="role-module-search" /></div>
            <div className="space-y-0.5 max-h-[60vh] overflow-y-auto">
              {modules.filter((m) => m.label.toLowerCase().includes(q.toLowerCase())).map((m) => (
                <button key={m.key} onClick={() => setSelModule(m.key)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${selModule === m.key ? "bg-cocoa/10 text-cocoa font-semibold" : "text-wmuted hover:bg-ivory"}`} data-testid={`role-module-${m.key}`}>
                  <span>{m.label}</span><span className="text-[11px]">{moduleCount(m.key)}</span>
                </button>
              ))}
            </div>
          </Panel>
          <Panel className="p-3 overflow-hidden">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="font-semibold text-cocoa">{curMod?.label}</div>
              {!readOnly && <div className="flex gap-1">{["read_only", "operator", "supervisor", "approver", "full", "clear"].map((p) => (
                <Button key={p} size="sm" variant="outline" className="h-7 text-xs border-taupe text-cocoa" onClick={() => applyPreset(selModule, p)} data-testid={`role-preset-${p}`}>{p === "read_only" ? "Read Only" : p === "clear" ? "Kosongkan" : p.charAt(0).toUpperCase() + p.slice(1)}</Button>
              ))}</div>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left border-b border-taupe/20">
                  <th className="py-2 pr-2">Resource</th>
                  {ACTION_ORDER.map((a) => <th key={a} className="px-1.5 text-center text-[11px] font-semibold text-wmuted">{ACTION_LABEL[a]}</th>)}
                </tr></thead>
                <tbody>
                  {(curMod?.resources || []).map((res) => (
                    <tr key={res.key} className="border-b border-taupe/10" data-testid={`role-res-${res.key}`}>
                      <td className="py-2 pr-2"><div className="text-cocoa font-medium">{res.label}</div><div className="text-[10px] text-wmuted font-mono">{res.key}</div></td>
                      {ACTION_ORDER.map((a) => (
                        <td key={a} className="px-1.5 text-center">
                          {res.actions.includes(a)
                            ? <Checkbox disabled={readOnly} checked={has(`${res.key}.${a}`)} onCheckedChange={(v) => toggle(`${res.key}.${a}`, !!v)} data-testid={`perm-${res.key}-${a}`} />
                            : <span className="text-taupe/30">·</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {tab === "scope" && (
        <Panel className="p-4 space-y-4" data-testid="role-scope-tab">
          <p className="text-sm text-wmuted">Batasi record yang boleh dilihat/dikerjakan role ini.</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div><div className="font-semibold text-cocoa mb-2">Cakupan organisasi</div>
              {[["own", "Data sendiri"], ["division", "Divisi sendiri"], ["all", "Seluruh divisi"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 py-1 text-sm cursor-pointer"><input type="radio" disabled={readOnly} checked={scope.organization === k} onChange={() => setScope((s) => ({ ...s, organization: k }))} data-testid={`scope-org-${k}`} />{l}</label>
              ))}
            </div>
            <div><div className="font-semibold text-cocoa mb-2">Channel penjualan</div>
              {(cat.channels || []).map((c) => (
                <label key={c} className="flex items-center gap-2 py-1 text-sm cursor-pointer"><Checkbox disabled={readOnly} checked={(scope.channels || []).includes(c)} onCheckedChange={(v) => setScope((s) => ({ ...s, channels: v ? [...(s.channels || []), c] : (s.channels || []).filter((x) => x !== c) }))} data-testid={`scope-channel-${c}`} />{c}</label>
              ))}
            </div>
          </div>
        </Panel>
      )}

      {tab === "sensitive" && (
        <Panel className="p-4 space-y-4" data-testid="role-sensitive-tab">
          <p className="text-sm text-wmuted">Permission ini berdiri sendiri meskipun user dapat membuka modulnya.</p>
          <div className="grid gap-4 md:grid-cols-2">
            {cat.sensitive_assets.map((g) => (
              <div key={g.group} className="rounded-lg border border-taupe/20 p-3">
                <div className="font-semibold text-cocoa mb-2">{g.group}</div>
                {g.items.map((it) => (
                  <label key={it.key} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                    <Checkbox disabled={readOnly} checked={has(it.key)} onCheckedChange={(v) => toggle(it.key, !!v)} data-testid={`sens-${it.key}`} />
                    <span>{it.label}</span>
                    {it.risk === "high" && <AlertTriangle className="w-3 h-3 text-status-reject" />}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {tab === "dashboard" && <DashboardConfig roleKey={detail.key} readOnly={readOnly} />}

      {tab === "users" && (
        <Panel className="p-0 overflow-hidden" data-testid="role-users-tab">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Nama</TableHead><TableHead>Divisi</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Akses</TableHead></TableRow></TableHeader>
            <TableBody>
              {detail.assigned_users.map((u) => (
                <TableRow key={u.id}><TableCell className="text-cocoa">{u.name}</TableCell><TableCell className="text-sm text-wmuted">{u.division_name || "-"}</TableCell>
                  <TableCell className="text-xs">{u.active === false ? <span className="text-status-reject">Nonaktif</span> : <span className="text-emerald-600">Aktif</span>}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" className="h-7 text-cocoa" onClick={() => setPreviewOpen(u.id)} data-testid={`role-user-preview-${u.id}`}>Effective Access</Button></TableCell>
                </TableRow>
              ))}
              {!detail.assigned_users.length && <TableRow><TableCell colSpan={4} className="text-center text-wmuted py-8">Belum ada pengguna dengan role ini.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Panel>
      )}

      {tab === "history" && (
        <Panel className="p-0 overflow-hidden" data-testid="role-history-tab">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Waktu</TableHead><TableHead>Oleh</TableHead><TableHead className="text-center">Ditambah</TableHead><TableHead className="text-center">Dicabut</TableHead><TableHead className="text-center">User Terdampak</TableHead><TableHead>Alasan</TableHead></TableRow></TableHeader>
            <TableBody>
              {detail.versions.map((v) => (
                <TableRow key={v.id}><TableCell className="text-xs">{(v.created_at || "").slice(0, 16).replace("T", " ")}</TableCell><TableCell className="text-sm">{v.actor}</TableCell>
                  <TableCell className="text-center text-emerald-600">+{(v.added || []).length}</TableCell><TableCell className="text-center text-status-reject">-{(v.removed || []).length}</TableCell>
                  <TableCell className="text-center">{v.affected_users}</TableCell><TableCell className="text-sm text-wmuted">{v.reason || "-"}</TableCell>
                </TableRow>
              ))}
              {!detail.versions.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-8">Belum ada riwayat perubahan.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Panel>
      )}

      {previewOpen && <PreviewDialog userId={typeof previewOpen === "string" ? previewOpen : null} users={detail.assigned_users} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}

const SIZE_LABEL = { S: "Kecil (S)", M: "Sedang (M)", L: "Lebar (L)" };
const SPAN_CLS = { S: "col-span-1", M: "col-span-2", L: "col-span-3" };

function DashboardConfig({ roleKey, readOnly }) {
  const [reg, setReg] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/dashboards/registry"), api.get(`/dashboards/role/${roleKey}/layout`)])
      .then(([r, l]) => { setReg(r.data); setItems(l.data.items || []); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleKey]);

  if (loading || !reg) return <Panel className="p-6 text-wmuted" data-testid="role-dashboard-tab">Memuat…</Panel>;

  const byKey = Object.fromEntries(reg.widgets.map((w) => [w.key, w]));
  const inLayout = new Set(items.map((i) => i.key));
  const library = reg.widgets.filter((w) => !inLayout.has(w.key));

  const addWidget = (w) => setItems((s) => [...s, { key: w.key, size: w.default_size, mask: w.maskable ? "masked" : "exact" }]);
  const removeWidget = (k) => setItems((s) => s.filter((i) => i.key !== k));
  const patch = (k, p) => setItems((s) => s.map((i) => i.key === k ? { ...i, ...p } : i));
  const move = (from, to) => setItems((s) => { const n = [...s]; const [it] = n.splice(from, 1); n.splice(to, 0, it); return n; });

  const onDrop = (to) => { if (dragIdx !== null && dragIdx !== to) move(dragIdx, to); setDragIdx(null); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/dashboards/role/${roleKey}/layout`, { items: items.map((i) => ({ key: i.key, size: i.size, mask: i.mask })) });
      toast.success("Layout dashboard disimpan"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="role-dashboard-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-wmuted flex items-center gap-1.5"><LayoutDashboard className="w-4 h-4" />Susun widget, ukuran, dan mode masking untuk dashboard role ini. Seret untuk mengubah urutan.</p>
        {!readOnly && <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="dash-config-save-btn"><Save className="w-4 h-4 mr-1" />{saving ? "Menyimpan…" : "Simpan Layout"}</Button>}
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] gap-4">
        {/* Active widgets */}
        <Panel className="p-3">
          <div className="font-semibold text-cocoa mb-2">Widget Aktif ({items.length})</div>
          <div className="space-y-2" data-testid="dash-config-active">
            {items.map((it, idx) => {
              const w = byKey[it.key]; if (!w) return null;
              return (
                <div key={it.key} draggable={!readOnly} onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
                  className="flex items-center gap-2 border border-taupe/20 rounded-lg p-2 bg-white" data-testid={`dash-item-${it.key}`}>
                  {!readOnly && <GripVertical className="w-4 h-4 text-taupe cursor-grab shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-cocoa truncate">{w.label}</div>
                    <div className="text-[10px] text-wmuted"><span className="uppercase">{w.type}</span> · {w.group}</div>
                  </div>
                  <select disabled={readOnly} value={it.size} onChange={(e) => patch(it.key, { size: e.target.value })}
                    className="text-xs border border-taupe/30 rounded px-1.5 py-1 bg-white" data-testid={`dash-size-${it.key}`}>
                    {w.sizes.map((s) => <option key={s} value={s}>{SIZE_LABEL[s]}</option>)}
                  </select>
                  {w.maskable && (
                    <select disabled={readOnly} value={it.mask} onChange={(e) => patch(it.key, { mask: e.target.value })}
                      className="text-xs border border-amber-300 rounded px-1.5 py-1 bg-amber-50 text-amber-800" title="Mode saat role tidak punya izin lihat angka penuh" data-testid={`dash-mask-${it.key}`}>
                      {reg.mask_modes.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                  )}
                  {!readOnly && <Button size="icon" variant="ghost" className="h-7 w-7 text-status-reject" onClick={() => removeWidget(it.key)} data-testid={`dash-remove-${it.key}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              );
            })}
            {!items.length && <div className="text-sm text-wmuted py-6 text-center">Belum ada widget. Tambahkan dari Pustaka Widget.</div>}
          </div>

          {/* Live preview */}
          <div className="mt-4 pt-3 border-t border-taupe/15">
            <div className="text-xs font-semibold text-wmuted uppercase tracking-wide mb-2">Pratinjau Tata Letak</div>
            <div className="grid grid-cols-3 gap-2" data-testid="dash-config-preview">
              {items.map((it) => {
                const w = byKey[it.key]; if (!w) return null;
                return <div key={it.key} className={`${SPAN_CLS[it.size]} rounded-md bg-cocoa/5 border border-cocoa/10 px-2 py-3 text-[11px] text-cocoa font-medium truncate`}>{w.label}{it.mask && it.mask !== "exact" && w.maskable ? ` · ${it.mask}` : ""}</div>;
              })}
              {!items.length && <div className="col-span-3 text-xs text-wmuted text-center py-4">—</div>}
            </div>
          </div>
        </Panel>

        {/* Library */}
        <Panel className="p-3">
          <div className="font-semibold text-cocoa mb-2">Pustaka Widget ({library.length})</div>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto" data-testid="dash-config-library">
            {library.map((w) => (
              <div key={w.key} className="flex items-center gap-2 border border-taupe/15 rounded-lg p-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-cocoa truncate">{w.label}{w.maskable && <AlertTriangle className="inline w-3 h-3 text-amber-500 ml-1" />}</div>
                  <div className="text-[10px] text-wmuted uppercase">{w.type}</div>
                </div>
                {!readOnly && <Button size="sm" variant="outline" className="h-7 text-xs border-taupe text-cocoa" onClick={() => addWidget(w)} data-testid={`dash-add-${w.key}`}><Plus className="w-3.5 h-3.5 mr-0.5" />Tambah</Button>}
              </div>
            ))}
            {!library.length && <div className="text-sm text-wmuted py-6 text-center">Semua widget sudah dipakai.</div>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Card({ label, value, badge }) {
  return (
    <div className="rounded-lg border border-taupe/25 bg-white p-3">
      <div className="text-[11px] font-semibold text-wmuted uppercase tracking-wide">{label}</div>
      <div className="text-base font-bold text-cocoa mt-0.5 flex items-center gap-2">{value}{badge && <span className="text-[10px] font-medium bg-taupe/20 text-cocoa px-1.5 py-0.5 rounded">{badge}</span>}</div>
    </div>
  );
}

function PreviewDialog({ userId, users, onClose }) {
  const [uid, setUid] = useState(userId || (users[0]?.id || ""));
  const [data, setData] = useState(null);
  useEffect(() => { if (!uid) return; setData(null); api.get(`/roles/effective-access/${uid}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [uid]);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="role-preview-dialog">
      <DialogHeader><DialogTitle className="font-heading text-cocoa">Effective Access Preview</DialogTitle></DialogHeader>
      {!userId && <select className="w-full border border-taupe/30 rounded-lg p-2 text-sm bg-white" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="preview-user-select">{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>}
      {!data ? <div className="text-wmuted py-4 text-center">Memuat…</div> : (
        <div className="space-y-3 text-sm">
          <div className="text-cocoa"><b>{data.user.name}</b> · role {data.user.role} · {data.user.division_name || "-"}</div>
          {data.is_owner ? <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded px-3 py-2 text-xs">System Owner — akses penuh ({data.effective_count} permission).</div>
            : <>
              <div className="text-xs text-wmuted">{data.effective_count} permission efektif. Sumber tiap izin ditampilkan di bawah.</div>
              {data.explicit_deny.length > 0 && <div className="text-xs text-status-reject">Explicit Deny: {data.explicit_deny.join(", ")}</div>}
              <div className="max-h-64 overflow-y-auto border border-taupe/20 rounded-lg divide-y divide-taupe/10" data-testid="preview-perm-list">
                {data.effective_perms.map((p) => (
                  <div key={p} className="px-2 py-1 flex items-center justify-between"><span className="font-mono text-[11px] text-cocoa">{p}</span><span className="text-[10px] text-wmuted">{(data.sources[p] || []).join(", ")}</span></div>
                ))}
              </div>
            </>}
        </div>
      )}
      <DialogFooter><Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Tutup</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
